import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useCustomers, useDeleteCustomer } from '../../hooks/useCustomers';
import { useAuthStore } from '../../stores/auth.store';
import DataTable from '../../components/common/DataTable';
import SearchInput from '../../components/common/SearchInput';
import Modal from '../../components/common/Modal';
import CustomerForm from './CustomerForm';
import type { Customer } from '@motra/shared';

interface CmField {
  key: string;
  label: string;
  type?: string;
}

export default function CustomersPage() {
  const { t } = useTranslation();
  const account = useAuthStore((s) => s.account);
  const { data: customers = [], isLoading } = useCustomers();
  const deleteCustomer = useDeleteCustomer();

  const [search, setSearch] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [editCustomer, setEditCustomer] = useState<Customer | null>(null);

  // Parse dynamic fields from account config
  const cmFields = useMemo<CmField[]>(() => {
    if (!account?.cm_field) return [{ key: 'name', label: 'Name' }, { key: 'phone', label: 'Phone' }, { key: 'email', label: 'Email' }];
    try {
      return JSON.parse(account.cm_field);
    } catch {
      return [{ key: 'name', label: 'Name' }, { key: 'phone', label: 'Phone' }, { key: 'email', label: 'Email' }];
    }
  }, [account?.cm_field]);

  // Get field value from customer metadata
  function getFieldValue(customer: Customer, key: string): string {
    if (!customer.metadata) return '';
    try {
      const meta = JSON.parse(customer.metadata);
      return meta[key] ?? '';
    } catch {
      return '';
    }
  }

  // Filter customers
  const filtered = useMemo(() => {
    if (!search) return customers;
    const q = search.toLowerCase();
    return customers.filter((c: Customer) => {
      if (!c.metadata) return false;
      try {
        const meta = JSON.parse(c.metadata);
        return Object.values(meta).some((v) =>
          String(v).toLowerCase().includes(q),
        );
      } catch {
        return false;
      }
    });
  }, [customers, search]);

  // Build dynamic columns from cm_field config
  const columns = [
    ...cmFields.slice(0, 4).map((field) => ({
      header: field.label,
      accessor: `metadata.${field.key}` as keyof Customer,
      render: (_val: unknown, row: Customer) => getFieldValue(row, field.key),
    })),
    {
      header: t('loyalty_points', 'Points'),
      accessor: 'loyalty_points' as keyof Customer,
      render: (val: unknown) => String(val ?? 0),
    },
    {
      header: t('credits', 'Credits'),
      accessor: 'credits' as keyof Customer,
      render: (val: unknown) => String(val ?? 0),
    },
    {
      header: 'Actions',
      accessor: 'id' as keyof Customer,
      render: (_val: unknown, row: Customer) => (
        <div className="action-btns">
          <button
            className="btn btn--sm btn--outline"
            onClick={(e) => {
              e.stopPropagation();
              setEditCustomer(row);
              setModalOpen(true);
            }}
          >
            {t('edit', 'Edit')}
          </button>
          <button
            className="btn btn--sm btn--danger"
            onClick={(e) => {
              e.stopPropagation();
              if (confirm('Delete this customer?')) {
                deleteCustomer.mutate(row.id);
              }
            }}
          >
            {t('delete', 'Delete')}
          </button>
        </div>
      ),
    },
  ];

  return (
    <div className="page">
      <div className="page-header">
        <h1 className="page-title">{t('customers', 'Customers')}</h1>
        <button
          className="btn btn--primary"
          onClick={() => {
            setEditCustomer(null);
            setModalOpen(true);
          }}
        >
          + {t('add', 'Add Customer')}
        </button>
      </div>

      <div className="page-filters">
        <SearchInput
          value={search}
          onChange={setSearch}
          placeholder="Search customers..."
        />
      </div>

      <DataTable
        columns={columns}
        data={filtered}
        loading={isLoading}
        onRowClick={(row) => {
          setEditCustomer(row);
          setModalOpen(true);
        }}
        rowKey={(row) => row.id}
        emptyMessage="No customers found"
      />

      <Modal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        title={editCustomer ? t('edit', 'Edit Customer') : t('add', 'Add Customer')}
        size="md"
      >
        <CustomerForm
          customer={editCustomer}
          cmFields={cmFields}
          onSaved={() => setModalOpen(false)}
          onCancel={() => setModalOpen(false)}
        />
      </Modal>
    </div>
  );
}
